import { useAuthenticator } from "@aws-amplify/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, SecretTextField, TextField } from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";

import { AuthScreenLayout } from "./AuthScreenLayout";
import {
  resetConfirmSchema,
  resetRequestSchema,
  type ResetConfirmValues,
  type ResetRequestValues,
} from "./schema";

/**
 * The password-reset screen, matching SignInScreen. Handles both machine steps:
 * `resetPassword` (request a code by email) and `confirmResetPassword` (enter
 * the code and a new password). Driven via `useAuthenticator`, so it replaces
 * Amplify's built-in reset UI without changing the underlying flow.
 */
export function ResetPasswordScreen() {
  const {
    route,
    submitForm,
    toSignIn,
    resendCode,
    error,
    validationErrors,
    isPending,
  } = useAuthenticator((context) => [
    context.route,
    context.submitForm,
    context.toSignIn,
    context.resendCode,
    context.error,
    context.validationErrors,
    context.isPending,
  ]);

  const isConfirmStep = route === "confirmResetPassword";

  const requestForm = useForm<ResetRequestValues>({
    resolver: zodResolver(resetRequestSchema),
    defaultValues: { username: "" },
  });
  const confirmForm = useForm<ResetConfirmValues>({
    resolver: zodResolver(resetConfirmSchema),
    defaultValues: { code: "", password: "", confirmPassword: "" },
  });

  const onRequest = ({ username }: ResetRequestValues) => {
    submitForm({ username });
  };

  // The schema catches the mismatch that Amplify reports via validationErrors
  // rather than error, which used to fail silently.
  const onConfirm = ({
    code,
    password,
    confirmPassword,
  }: ResetConfirmValues) => {
    submitForm({
      confirmation_code: code,
      password,
      confirm_password: confirmPassword,
    });
  };

  // Amplify reports some failures via validationErrors rather than error, so
  // surface both (plus our local checks) or they'd be swallowed.
  const messages = [
    ...new Set(
      [
        typeof error === "string" ? error : null,
        ...Object.values(validationErrors ?? {}).flat(),
      ].filter((m): m is string => Boolean(m)),
    ),
  ];

  return (
    <AuthScreenLayout
      subtitle={
        isConfirmStep
          ? "Enter the code we sent and choose a new password."
          : "Enter your account email or username and we'll send you a reset code."
      }
      title={isConfirmStep ? "Check your email" : "Reset your password"}
    >
      <form
        className="flex flex-col gap-lg"
        onSubmit={
          isConfirmStep
            ? confirmForm.handleSubmit(onConfirm)
            : requestForm.handleSubmit(onRequest)
        }
        noValidate
      >
        {isConfirmStep ? (
          <>
            <Controller
              control={confirmForm.control}
              name="code"
              render={({ field, fieldState }) => (
                <TextField
                  assistiveText={fieldState.error?.message}
                  inputProps={{
                    autoComplete: "one-time-code",
                    className: "bg-background-primary",
                    placeholder: "123456",
                  }}
                  isInvalid={fieldState.invalid}
                  label="Verification code"
                  isRequired
                  {...field}
                />
              )}
            />
            <Controller
              control={confirmForm.control}
              name="password"
              render={({ field, fieldState }) => (
                <SecretTextField
                  assistiveText={fieldState.error?.message}
                  inputProps={{
                    autoComplete: "new-password",
                    className: "bg-background-primary",
                  }}
                  isInvalid={fieldState.invalid}
                  label="New password"
                  isRequired
                  {...field}
                />
              )}
              rules={{ deps: ["confirmPassword"] }}
            />
            <Controller
              control={confirmForm.control}
              name="confirmPassword"
              render={({ field, fieldState }) => (
                <SecretTextField
                  assistiveText={fieldState.error?.message}
                  inputProps={{
                    autoComplete: "new-password",
                    className: "bg-background-primary",
                  }}
                  isInvalid={fieldState.invalid}
                  label="Confirm password"
                  isRequired
                  {...field}
                />
              )}
              rules={{ deps: ["password"] }}
            />
          </>
        ) : (
          <Controller
            control={requestForm.control}
            name="username"
            render={({ field, fieldState }) => (
              <TextField
                assistiveText={fieldState.error?.message}
                inputProps={{
                  autoComplete: "username",
                  className: "bg-background-primary",
                  placeholder: "you@company.com or username",
                }}
                isInvalid={fieldState.invalid}
                label="Email or username"
                type="text"
                isRequired
                {...field}
              />
            )}
          />
        )}

        {messages.length > 0 ? (
          <div className="flex flex-col gap-xxs" role="alert">
            {messages.map((m) => (
              <p className="text-label-medium text-foreground-negative" key={m}>
                {m}
              </p>
            ))}
          </div>
        ) : null}

        <Button
          className="flex items-center justify-center"
          data-testid="reset-password-submit"
          isPending={isPending}
          type="submit"
          variant="primary"
        >
          {isConfirmStep ? "Reset password" : "Send code"}
        </Button>

        <div className="flex items-center justify-center gap-md">
          {isConfirmStep ? (
            <Button
              className="h-auto p-0 font-normal"
              onPress={() => {
                // The code in the box is superseded the moment a new one is sent.
                confirmForm.resetField("code");
                resendCode();
              }}
              variant="link"
            >
              Resend code
            </Button>
          ) : null}
          <Button
            className="h-auto p-0 font-normal"
            onPress={toSignIn}
            variant="link"
          >
            Back to sign in
          </Button>
        </div>
      </form>
    </AuthScreenLayout>
  );
}
